export const getBasicDetailsInputs = (
  buildingOptions,
  propertyOptions,
  selectedBuildingId,
  tenantType
) => {
  const buildingIdNum = Number(selectedBuildingId);

  const filteredPropertyOptions = propertyOptions
    .filter((p) => p.building_id === buildingIdNum)
    .map((p) => ({
      value: p.id.toString(),
      label: p.property_name,
    }));

  return [
    /* ------- Tenant Information ------- */
    {
      _key: "tenant_first_name",
      _name: "First name",
      _type: "text",
      _placeholder: "John",
      _helperText: "As shown on official documents.",
      _mandatory: true,
      _gridSize: 6,
    },
    {
      _key: "tenant_last_name",
      _name: "Last name",
      _type: "text",
      _placeholder: "Doe",
      _helperText: "As shown on official documents.",
      _mandatory: true,
      _gridSize: 6,
    },
    {
      _key: "tenant_contact",
      _name: "Phone number",
      _type: "text",
      _placeholder: "9876543210",
      _helperText: "We'll use this to contact the tenant.",
      _mandatory: true,
      _gridSize: 6,
    },
    {
      _key: "tenant_type",
      _name: "Tenant type",
      _type: "dynamic-drop-down",
      _placeholder: "Select a tenant type",
      _helperText: "Choose the applicable category.",
      _options: tenantType,
      _mandatory: true,
      _gridSize: 6,
    },

    /* ------- Property Details ------- */
    {
      _key: "building_id",
      _name: "Building",
      _type: "dynamic-drop-down",
      _placeholder: "Select a building",
      _helperText: "Only assigned properties will be shown.",
      _mandatory: true,
      _options: buildingOptions,
      _gridSize: 6,
    },
    {
      _key: "property_id",
      _name: "Property",
      _type: "dynamic-drop-down",
      _placeholder: "Select a property",
      _helperText: "Choose a property from the selected building.",
      _mandatory: true,
      _options: filteredPropertyOptions,
      _gridSize: 6,
      _disabled: !selectedBuildingId,
    },
    {
      _key: "entry_date",
      _name: "Move-in date",
      _type: "date",
      _placeholder: "Select a date",
      _helperText: "When the tenancy begins.",
      _mandatory: true,
      _gridSize: 6,
    },
    {
      _key: "exit_date",
      _name: "Move-out date",
      _type: "date",
      _placeholder: "Select a date",
      _helperText: "Leave blank if the tenancy is ongoing.",
      _mandatory: false,
      _gridSize: 6,
    },

    /* ------- Rent Details ------- */
    {
      _key: "rent_amount",
      _name: "Monthly rent",
      _type: "amount",
      _placeholder: "12,000",
      _helperText: "Monthly rent before additional charges.",
      _mandatory: true,
      _gridSize: 6,
    },
    {
      _key: "maintenance_charges",
      _name: "Maintenance fee",
      _type: "amount",
      _placeholder: "1,000",
      _helperText: "Optional monthly maintenance fee.",
      _mandatory: false,
      _gridSize: 6,
    },
    {
      _key: "electricity_per_unit_charge",
      _name: "Electricity rate",
      _type: "number",
      _placeholder: "8.50",
      _helperText: "Cost per electricity unit.",
      _mandatory: false,
      _gridSize: 6,
    },
    {
      _key: "monthly_electricity_bill",
      _name: "Electricity charge",
      _type: "amount",
      _placeholder: "2,500",
      _helperText: "Optional fixed monthly electricity charge.",
      _mandatory: false,
      _gridSize: 6,
    },
    {
      _key: "round_off",
      _name: "Adjustment",
      _type: "text",
      _placeholder: "Calculated automatically",
      _helperText: "Updated automatically.",
      _mandatory: false,
      _options: {
        readOnly: true,
      },
      _gridSize: 6,
    },
    {
      _key: "total_rent_amount",
      _name: "Total monthly rent",
      _type: "amount",
      _placeholder: "Calculated automatically",
      _helperText: "Includes all applicable charges.",
      _mandatory: true,
      _options: {
        readOnly: true,
      },
      _gridSize: 12,
    },
  ];
};

export const getTaxComplianceDetailsInputs = (intervalOptions,) => {

  return [

    /* ------- Tax & Compliance ------- */
    {
      _key: "apply_gst",
      _name: "Apply GST",
      _type: "checkbox",
      _placeholder: "GST",
      _helperText: "Enable if GST applies to this tenancy",
      _mandatory: false,
      _gridSize: 6,
    },
    {
      _key: "apply_tds",
      _name: "Apply TDS Compliance",
      _type: "checkbox",
      _placeholder: "Apply TDS Compliance",
      _helperText: "Enable to deduct TDS as per tax regulations",
      _mandatory: false,
      _gridSize: 6,
    },
    {
      _key: "tds_type",
      _name: "TDS category",
      _type: "dynamic-drop-down",
      _placeholder: "Select a type",
      _helperText: "Required when TDS is enabled.",
      _mandatory: true,
      _options: [{
        value: "BUSINESS",
        label: "Business",
      }, {
        value: "INDIVIDUAL",
        label: "Individual",
      }],
      _gridSize: 6,
    },
    {
      _key: "gst_number",
      _name: "GST number",
      _type: "text",
      _placeholder: "22AAAAA0000A1Z5",
      _helperText: "Required if GST is applicable.",
      _mandatory: true,
      _gridSize: 6
    },
    {
      _key: "pan_number",
      _name: "PAN number",
      _type: "text",
      _placeholder: "ABCDE1234F",
      _helperText: "Used for tax reporting.",
      _mandatory: true,
      _gridSize: 6
    },
    {
      _key: "aadhar_number",
      _name: "Aadhaar number",
      _type: "text",
      _placeholder: "1234 5678 9012",
      _helperText: "Enter the tenant's Aadhaar number",
      _mandatory: true,
      _gridSize: 6
    },

    /* ------- Contract Settings ------- */
    {
      _key: "annual_increment_type",
      _name: "Rent increase schedule",
      _type: "dynamic-drop-down",
      _placeholder: "Select a schedule",
      _helperText: "How often rent increases.",
      _options: intervalOptions,
      _mandatory: false,
      _gridSize: 6,
    },
    {
      _key: "annual_increment_date",
      _name: "Next increase date",
      _type: "date",
      _placeholder: "Calculated automatically",
      _helperText: "Calculated based on the tenancy start date.",
      _mandatory: false,
      _options: {
        readOnly: true,
      },
      _gridSize: 6,
    },
    {
      _key: "increment_precentage",
      _name: "Increase (%)",
      _type: "text",
      _placeholder: "20",
      _helperText: "Annual rent increase in %.",
      _mandatory: false,
      _gridSize: 6,
    },
    {
      _key: "notes",
      _name: "Notes",
      _type: "text",
      _placeholder: "Add a note...",
      _helperText: "Add any additional information or remarks",
      _mandatory: false,
      _options: {},
      _gridSize: 6,
    },
  ];
};
